import { Subcategory } from "@/types/LabReport"
import { useState } from "react"
import { useParams } from "react-router-dom"
import { useApp } from "../AppProvider"
import { ErrorMessage } from "../components/common/Feedback/ErrorMessage"
import { LoadingMessage } from "../components/common/Feedback/LoadingMessage"
import Modal from "../components/common/Modal"
import { ShareModal } from "../components/common/ShareModal"
import { EmailEditor } from "../components/emails/EmailEditor"
import { Recommendations } from "../components/results/Recommendations"
import { ResultsCharts } from "../components/results/ResultsCharts"
import { ResultsHeader } from "../components/results/ResultsHeader"
import { ResultsOverview } from "../components/results/ResultsOverview"
import { useGetLabReport } from "../hooks/useLabReports"

export const LabReportPage = () => {
  const { token, isAnonymous, anonToken, shareData } = useApp()
  const { organizationId, reportId } = useParams()

  // Use share data for organization and report ID if in anonymous mode
  const effectiveOrgId = isAnonymous && shareData ? shareData.organizationId : organizationId!

  const effectiveReportId = isAnonymous && shareData ? shareData.resourceId : reportId!

  const resultsQuery = useGetLabReport(effectiveOrgId, effectiveReportId, token, anonToken)
  const results = resultsQuery?.data

  const [isEmailEditorOpen, setIsEmailEditorOpen] = useState(false)
  const [isShareModalOpen, setIsShareModalOpen] = useState(false)

  if (resultsQuery.isLoading) {
    return <LoadingMessage />
  }

  if (resultsQuery.isError || !results?.labReport) {
    return <ErrorMessage message={resultsQuery?.error?.message ?? "Unknown application error"} />
  }

  const subcategories = results.labReport?.subcategories
    // Hide empty subcategories
    .filter((subcategory) => subcategory.tests.length > 0)
    // Sort by priority list
    .sort((a, b) => getPriorityIndex(a) - getPriorityIndex(b))

  return (
    <div className="flex flex-col gap-y-5">
      <ResultsHeader
        results={results.labReport}
        setIsEmailEditorOpen={setIsEmailEditorOpen}
        setIsShareModalOpen={setIsShareModalOpen}
      />
      <div className="grid grid-cols-1 gap-x-3 gap-y-4 md:grid-cols-12">
        <div className="card col-span-1 md:col-span-4 lg:col-span-4">
          <ResultsOverview results={results.labReport} patient={results.patient} />
        </div>
        <div className="card col-span-1 max-h-[400px] overflow-y-auto md:col-span-8 lg:col-span-8">
          <Recommendations recommendations={results.recommendations ?? []} />
        </div>
        {subcategories.length > 0 ? (
          <>
            {subcategories.map((subcategory, i) => (
              <div key={i} className="card col-span-1 md:col-span-12">
                <ResultsCharts results={subcategory} />
              </div>
            ))}
          </>
        ) : (
          <div className="card col-span-1 md:col-span-12">No test results found.</div>
        )}
      </div>
      {!isAnonymous && (
        <>
          <Modal open={isEmailEditorOpen} setOpen={() => setIsEmailEditorOpen(false)}>
            <EmailEditor
              patient={results.patient || (results.labReport.patient as any)}
              submitCb={() => setIsEmailEditorOpen(false)}
            />
          </Modal>
          <ShareModal
            open={isShareModalOpen}
            setOpen={setIsShareModalOpen}
            resourceType="lab-report"
            resourceId={results.labReport.id}
            title="Share Lab Report"
          />
        </>
      )}
    </div>
  )
}

/**
 * Priority list of known test types in desired display order.
 * Each has:
 * - canonical name
 * - list of aliases for name matching
 * - list of known IDs
 */
const TEST_TYPE_PRIORITY = [
  {
    name: "Complete Blood Count",
    ids: ["12171"],
    aliases: ["cbc", "c.b.c"],
  },
  {
    name: "Chemistry",
    ids: ["12675"],
    aliases: ["superchem", "chem"],
  },
  {
    name: "T4",
    ids: ["12211"],
    aliases: ["t-4"],
  },
  {
    name: "Urinalysis",
    ids: ["12279"],
    aliases: ["ua", "urine analysis"],
  },
]

/**
 * Build a map for quick lookup by ID and alias.
 * Keys are stringified IDs or lowercase aliases.
 */
const PRIORITY_MAP = new Map()
TEST_TYPE_PRIORITY.forEach((item, index) => {
  item.ids.forEach((id) => PRIORITY_MAP.set(id.toString(), index))
  item.aliases.forEach((alias) => PRIORITY_MAP.set(alias.toLowerCase(), index))
})

/**
 * Get the sort index for a test type based on ID or name.
 * Unknown types are sorted after known ones.
 */
function getPriorityIndex(testType: Subcategory) {
  const id = testType.id?.toString()
  const name = testType.name?.toLowerCase()

  if (id && PRIORITY_MAP.has(id)) return PRIORITY_MAP.get(id)
  if (name && PRIORITY_MAP.has(name)) return PRIORITY_MAP.get(name)

  // Loosely match on aliases (case insensitive, partial match)
  // Could be improved by implementing fuzzy search
  for (const [key, index] of PRIORITY_MAP.entries()) {
    if (isNaN(key) && name && name.includes(key)) {
      return index
    }
  }

  return Infinity // Unknown type
}
